import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, Handshake, ArrowRight, Building2, KeyRound } from "lucide-react";
import SearchBar from "../components/SearchBar";
import PropertyCard from "../components/PropertyCard";
import { services } from "../data/content";
import api from "../api";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/properties?limit=6").then((r) => setFeatured(r.data.properties)).catch(() => {});
  }, []);

  return (
    <main className="bg-ink">
      {/* HERO */}
      <section className="pt-32 pb-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-500/10 text-brand-600 text-sm font-medium mb-6">
              Pune's Premium Property Marketplace
            </span>
            <h1 className="font-display text-5xl md:text-6xl font-bold leading-[1.05] text-navy">
              Find a home that <span className="text-brand-500">fits your life</span> in Pune.
            </h1>
            <p className="text-neutral-500 text-lg mt-6 max-w-xl">
              Buy or rent flats, houses and commercial spaces. Verified listings,
              trusted agents, and a search built around you.
            </p>
            <div className="flex flex-wrap gap-8 mt-8 text-neutral-500">
              <div><span className="text-2xl font-bold text-neutral-900">500+</span><br />Listings</div>
              <div><span className="text-2xl font-bold text-neutral-900">24</span><br />Pune Areas</div>
              <div><span className="text-2xl font-bold text-neutral-900">100%</span><br />Verified</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <img src="/hero_construction_bg.png" alt="Pune real estate" className="rounded-3xl shadow-card w-full h-[440px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-soft border border-neutral-200 px-5 py-4 hidden sm:block">
              <p className="text-sm text-neutral-500">Trusted by</p>
              <p className="font-bold text-navy">my Property Pune</p>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="mt-12">
          <SearchBar />
        </motion.div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-6">
        {[
          { to: "/properties?listing_type=Buy", icon: Building2, title: "Buy a Property", desc: "Own your dream flat, house or commercial space across Pune." },
          { to: "/properties?listing_type=Rent", icon: KeyRound, title: "Rent a Property", desc: "Flexible rentals for every budget and lifestyle." },
        ].map((c) => (
          <Link key={c.title} to={c.to} data-testid={`home-category-${c.title.includes("Buy") ? "buy" : "rent"}`}
            className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-surface p-8 shadow-card hover:-translate-y-1 transition-transform">
            <c.icon className="w-10 h-10 text-brand-500 mb-4" />
            <h3 className="text-2xl font-bold text-neutral-900 mb-2">{c.title}</h3>
            <p className="text-neutral-500 mb-6">{c.desc}</p>
            <span className="inline-flex items-center gap-2 text-brand-600 font-medium">
              Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </section>

      {/* FEATURED */}
      <section className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Featured in Pune</h2>
            <p className="text-neutral-500 mt-2">Handpicked homes ready for you.</p>
          </div>
          <Link to="/properties" className="text-brand-600 font-medium flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* SERVICES (original content) */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-brand-600 font-medium mb-1">my Property Pune</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Construction & Renovation</h2>
            <p className="text-neutral-500 mt-2 max-w-xl">A comprehensive range of premium construction and renovation services for modern spaces.</p>
          </div>
          <Link to="/services" className="text-brand-600 font-medium flex items-center gap-1 hover:gap-2 transition-all">
            All services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.slice(0, 4).map((s) => (
            <div key={s.title} className="group bg-surface border border-neutral-200 rounded-2xl overflow-hidden shadow-card hover:-translate-y-1 transition-transform">
              <div className="h-40 overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <span className="text-xs font-semibold uppercase tracking-wide text-brand-500">{s.category}</span>
                <h3 className="font-bold text-neutral-900 mt-1">{s.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-6xl mx-auto px-6 pb-10">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Verified Listings", desc: "Every property is checked so you deal only with genuine owners and agents." },
            { icon: TrendingUp, title: "Right Price", desc: "Transparent pricing and market insights to help you decide with confidence." },
            { icon: Handshake, title: "Trusted Support", desc: "Backed by my Property Pune — Pune's real-estate & property experts." },
          ].map((f) => (
            <div key={f.title} className="bg-surface border border-neutral-200 rounded-2xl p-8 shadow-card">
              <f.icon className="w-9 h-9 text-brand-500 mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">{f.title}</h3>
              <p className="text-neutral-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-10">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-500 to-brand-600 p-10 md:p-14">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white max-w-lg">Have a property to list?</h2>
          <p className="text-white/80 mt-3 max-w-md">Register as an Owner or Agent and reach thousands of buyers and tenants across Pune.</p>
          <Link to="/register" data-testid="home-list-property-cta"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-white text-neutral-900 font-semibold rounded-full hover:scale-105 transition-transform">
            List Your Property <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
